import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { useAuth } from './AuthContext'; // Using the standard Auth context in this project
import api, { apiCache } from '../services/api';
import { playRingTone } from '../Components/common/IncomingCallPopup';
import { PhoneIncoming } from 'lucide-react';

import { SocketContext } from './SocketContextInstance';

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Active incoming Exotel calls — drives IncomingCallPopup
  const [incomingCalls, setIncomingCalls] = useState([]);
  const { user } = useAuth(); // Assuming useAuth exposes the logged in user

  useEffect(() => {
    // Determine the user ID (Backend uses 'id', some structures use '_id')
    const userId = user?.id || user?._id;

    // Only connect if we have a user
    if (!userId) {
      return;
    }

    const socketUrl = import.meta.env.VITE_API_URL
      ? import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')
      : (window.location.hostname === 'localhost' ? 'http://localhost:8039' : window.location.origin);

    const socketInstance = io(socketUrl, {
      path: '/api/socket.io',
      withCredentials: true,
      transports: (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? ['websocket', 'polling']
        : ['websocket']  // Force WebSocket on production to bypass AWS Load Balancer sticky session issues
    });

    socketInstance.on('connect', () => {
      const currentUserId = user?.id || user?._id;

      // Authentication
      socketInstance.emit('authenticate', currentUserId);

      if (user.role) {
        // Match backend normalization (lowercase, trim, underscores)
        const normalizedRole = user.role.toLowerCase().trim().replace(/\s+/g, '_');

        socketInstance.emit('joinRoleRoom', normalizedRole);

        // Diagnostic Toast (only once per session)
        toast.success(`Real-time Sync Active: Connected as ${user.role}`, {
          id: 'socket-status',
          duration: 3000,
          icon: '🔌'
        });
      } else {
        console.warn('⚠️ Socket.io: No role found for user. Room joining skipped.');
      }
    });

    socketInstance.on('connect_error', (err) => {
      console.error('%c❌ Socket.io Connection Error:', 'color: #ef4444; font-weight: bold', err.message);
    });

    socketInstance.on('disconnect', (reason) => {
      console.warn(`%c🔌 Socket.io: Disconnected! Reason: ${reason}`, 'color: #f59e0b; font-weight: bold');
    });

    setSocket(socketInstance);

    // Populate history on initial mount
    const fetchHistory = async () => {
      try {
        const response = await api.get('/dashboard/activity');
        if (response.data?.success) {
          const rawHistory = response.data.data || [];
          const history = Array.isArray(rawHistory) ? rawHistory.map(log => ({
            ...log,
            timestamp: log.createdAt || log.timestamp
          })) : [];
          setNotifications(history);
          // Calculate initial unread count
          const unread = history.filter(n => !n.read).length;
          setUnreadCount(unread);
        }
      } catch (e) {
        console.warn('Notification History Not Available:', e.message);
      }
    };

    fetchHistory();

    // Cleanup when component unmounts or user changes
    return () => {
      socketInstance.disconnect();
    };
  }, [user?.id, user?._id, user?.role]);

  // Hook up Global Listeners here
  useEffect(() => {
    if (!socket) return;

    const addNotification = (notif) => {
      // 🚫 Skip if the notification is from the currently logged-in user
      // (We don't want to see our own actions as notifications)
      const actorId = notif.user?.toString() || notif.actorId?.toString();
      const currentUserId = user?.id?.toString() || user?._id?.toString();
      if (actorId && currentUserId && actorId === currentUserId) {
        return false;
      }

      // 🛡️ ROLE-BASED VISIBILITY FILTERING
      const isAdmin = user?.role === 'admin';
      const isLeadRelated = !!(notif.leadId || notif.entityId);
      const isAssigned = (
        notif.assignedCustomerCareUserId === currentUserId ||
        notif.creatorUserId === currentUserId ||
        notif.assignedSurgeonUserId === currentUserId ||
        notif.assignedAnesthesiologistUserId === currentUserId ||
        notif.assignedHospitalUserId === currentUserId ||
        notif.leadProviderUserId === currentUserId
      );

      if (!isAdmin && isLeadRelated && !isAssigned) {
        return false;
      }

      const newNotif = {
        ...notif,
        id: notif._id || Date.now(), // Use real ID if available, else numeric fallback for local UI
        timestamp: new Date(),
        read: false
      };
      setNotifications(prev => [newNotif, ...prev].slice(0, 50)); // Keep last 50
      setUnreadCount(prev => prev + 1);
      return true; // Notification was accepted
    };

    const getActionColors = (action) => {
      const act = (action || '').toLowerCase();
      if (act.includes('delete') || act.includes('remove') || act.includes('fail')) {
        return { badgeBg: 'bg-red-500/20', badgeText: 'text-red-400', badgeBorder: 'border-red-500/30', titleText: 'text-red-300', descText: 'text-red-50', icon: '🗑️', label: 'Deleted' };
      }
      if (act.includes('create') || act.includes('add') || act.includes('success') || act.includes('paid') || act.includes('verify') || act.includes('approve')) {
        return { badgeBg: 'bg-emerald-500/20', badgeText: 'text-emerald-400', badgeBorder: 'border-emerald-500/30', titleText: 'text-emerald-300', descText: 'text-emerald-50', icon: '✨', label: 'Success' };
      }
      if (act.includes('update') || act.includes('edit') || act.includes('assign')) {
        return { badgeBg: 'bg-blue-500/20', badgeText: 'text-blue-400', badgeBorder: 'border-blue-500/30', titleText: 'text-blue-300', descText: 'text-blue-50', icon: '🔄', label: 'Update' };
      }
      return { badgeBg: 'bg-slate-500/20', badgeText: 'text-slate-300', badgeBorder: 'border-slate-500/30', titleText: 'text-slate-300', descText: 'text-white', icon: '🛡️', label: 'System' };
    };

    const handleAppointmentUpdated = (data) => {
      apiCache.invalidateByUrl('/appointments');
      apiCache.invalidateByUrl('/surgeries');
      const accepted = addNotification({
        ...data,
        description: data.description || `Appointment ${data.bookingId || ''} status: ${data.status?.replace(/_/g, ' ') || 'updated'}`
      });
      if (accepted) {
        const style = getActionColors(data.status || data.action || '');
        toast.success(
          <div className="text-left min-w-[200px] sm:min-w-[260px]">
            <p className={`text-[9px] sm:text-[10px] font-black ${style.titleText} uppercase tracking-widest mb-1`}>Appointment Update</p>
            <p className={`text-xs sm:text-sm font-bold ${style.descText} line-clamp-2`}>{data.description}</p>
          </div>,
          { icon: style.icon, duration: 5000 }
        );
      }
    };

    const handleSystemActivity = (data) => {
      apiCache.invalidateByUrl('/dashboard');
      if (user?.role === 'admin') {
        const accepted = addNotification(data);
        if (accepted) {
          const style = getActionColors(data.action);
          toast(
            <div className="min-w-[200px] sm:min-w-[280px]">
              <div className="flex items-center gap-2 mb-1">
                <span className={`${style.badgeBg} ${style.badgeText} ${style.badgeBorder} px-2 py-0.5 rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-tighter shadow-sm border whitespace-nowrap`}>
                  {style.label}
                </span>
                <span className={`text-[9px] sm:text-[10px] font-black ${style.titleText} uppercase truncate`}>
                  {data.action?.replace(/_/g, ' ')}
                </span>
              </div>
              <p className={`text-xs sm:text-sm font-bold ${style.descText} line-clamp-2`}>{data.description}</p>
            </div>,
            { icon: style.icon, duration: 5000 }
          );
        }
      }
    };

    const handlePersonalNotification = (data) => {
      const accepted = addNotification(data);
      if (accepted) {
        const style = getActionColors(data.action);
        toast.success(
          <div className="text-left min-w-[200px] sm:min-w-[240px]">
            <p className={`text-[9px] sm:text-[10px] font-black ${style.titleText} uppercase tracking-widest mb-1`}>Update Alert</p>
            <p className={`text-xs sm:text-sm font-bold ${style.descText} line-clamp-2`}>{data.description}</p>
          </div>,
          { icon: style.icon, duration: 5000 }
        );
      }
    };

    const handleGlobalNotification = (data) => {
      return;
    };

    const handleLeadUpdated = (data) => {
      apiCache.invalidateByUrl('/leads');
      const accepted = addNotification({
        ...data,
        description: data.description || `Lead ${data.patientName || ''} was updated: ${data.action?.replace(/_/g, ' ')}`
      });

      if (accepted && (data.status === 'paid' || data.action === 'payment_verified')) {
        const pName = data.patientName || 'Patient';
        toast.success(`Payment Received: ${pName}'s pipeline advanced to PAID!`, {
          icon: '💰',
          duration: 6000
        });
      }
    };

    // ── Exotel: Incoming Call ────────────────────────────────────────────────
    const handleIncomingCall = (data) => {
      // Play ringtone
      playRingTone();

      // Add to active incoming calls list (shown in popup)
      setIncomingCalls(prev => {
        // Avoid duplicate if Exotel sends the webhook twice
        if (prev.some(c => c.callSid === data.callSid)) return prev;
        return [data, ...prev];
      });

      // Also show a toast for visibility
      toast(
        <div className="text-left min-w-[200px]">
          <p className="text-[10px] font-black text-violet-300 uppercase tracking-widest mb-1">📞 Incoming Call</p>
          <p className="text-sm font-bold text-white">{data.patientName || 'Unknown Caller'}</p>
          <p className="text-[11px] text-white/50 font-mono">{data.callerNumber}</p>
        </div>,
        { icon: '📲', duration: 8000 }
      );
    };

    // ── Exotel: Call Ended ───────────────────────────────────────────────────
    const handleCallEnded = (data) => {
      // Remove from active incoming calls
      setIncomingCalls(prev => prev.filter(c => c.callSid !== data.callSid));

      const durationText = data.duration > 0
        ? `${Math.floor(data.duration / 60)}m ${data.duration % 60}s`
        : '0s';
      const statusLabel = {
        answered: 'Answered',
        completed: 'Completed',
        'call-fatal': 'Call Fatal (No Talk)',
        missed: 'Missed Call',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
        busy: 'Busy',
        failed: 'Failed',
        waiting: 'Waiting',
        ringing: 'Ringing',
        'no-answer': 'No Answer'
      }[data.status] || data.status;

      toast.success(
        <div className="text-left">
          <p className="text-[10px] font-black text-emerald-300 uppercase tracking-widest mb-1">Call Ended</p>
          <p className="text-sm font-bold text-white">{statusLabel} · {durationText}</p>
        </div>,
        { icon: '📞', duration: 5000 }
      );
    };

    socket.on('system_activity', handleSystemActivity);
    socket.on('personal_notification', handlePersonalNotification);
    socket.on('global_notification', handleGlobalNotification);
    socket.on('lead_updated', handleLeadUpdated);
    socket.on('appointment_updated', handleAppointmentUpdated);
    socket.on('incoming_call', handleIncomingCall);
    socket.on('call_ended', handleCallEnded);

    return () => {
      socket.off('system_activity');
      socket.off('personal_notification');
      socket.off('global_notification');
      socket.off('lead_updated');
      socket.off('appointment_updated');
      socket.off('incoming_call');
      socket.off('call_ended');
    };
  }, [socket, user?.id, user?._id, user?.role]);

  const markAsRead = useCallback(async (logId) => {
    try {
      setNotifications(prev => prev.map(n =>
        (n._id === logId || n.id === logId) ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));

      const isObjectId = typeof logId === 'string' && /^[0-9a-fA-F]{24}$/.test(logId);
      if (isObjectId) {
        await api.put('/auth/notifications/mark-read', { logId });
      }
    } catch (e) {
      console.error('Failed to mark notification as read:', e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      setUnreadCount(0);
      setNotifications([]);
      await api.put('/auth/notifications/clear');
      toast.success('Notification center cleared');
    } catch (e) {
      console.error('Failed to clear notifications:', e);
      toast.error('Failed to clear notifications');
    }
  }, []);

  // Dismiss a specific incoming call popup
  const dismissIncomingCall = useCallback((callSid) => {
    setIncomingCalls(prev => prev.filter(c => c.callSid !== callSid));
  }, []);

  const contextValue = useMemo(() => ({
    socket,
    notifications,
    unreadCount,
    markAllRead,
    markAsRead,
    // Exotel call state
    incomingCalls,
    dismissIncomingCall
  }), [socket, notifications, unreadCount, markAllRead, markAsRead, incomingCalls, dismissIncomingCall]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};
